import Axios from 'axios';
import { getLocalStorage, prefixedKey } from '@tools/localStorage';
import { TOKEN_STORAGE_KEY } from '@tools/constants';

const controllers = {};

/**
 * What a cancelled request resolves to.
 *
 * A cancellation is not an error here — a newer request superseded this one — so it resolves rather
 * than rejects, and callers that ignore the result simply do nothing. That is deliberate and load
 * bearing: making it reject would turn every existing call site that quietly ignores the outcome
 * into an unhandled rejection. Callers that DO read the result must compare against this constant
 * before touching the value; only requests made with a `cancelName` can ever see it.
 */
export const REQUEST_CANCELLED = -1;

export default async (
  options,
  { returnFullResult = false, baseURL = window.ELLONA_SETTINGS.apiUrl, cancelName = null } = {}
) => {
  /*
    Built as a new object rather than assigned onto `options`. This used to write `headers` and
    `signal` straight onto the caller's object, so a service holding a reusable options literal had
    a stale auth token — and an aborted signal — baked into it for every later call.
  */
  const headers =
    baseURL === window.ELLONA_SETTINGS.apiUrl
      ? {
          ...options.headers,
          'x-auth': getLocalStorage(prefixedKey(TOKEN_STORAGE_KEY)),
          'Content-Type': 'application/json'
        }
      : { ...options.headers };

  let controller;
  if (cancelName) {
    if (controllers[cancelName]) {
      controllers[cancelName].abort();
      // console.warn('Request cancelling:', cancelName);
    }
    controller = new AbortController();
    controllers[cancelName] = controller;
  }

  try {
    const res = await Axios({
      ...options,
      headers,
      url: baseURL + options.url,
      signal: controller ? controller.signal : null
    });
    return returnFullResult ? res : res.data;
  } catch (err) {
    if (Axios.isCancel(err)) {
      console.warn('Request canceled:', err);
      return REQUEST_CANCELLED;
    }
    console.error('Services error :', err);
    throw err;
  } finally {
    // Drop our own controller, but never one a newer request has already replaced.
    if (cancelName && controllers[cancelName] === controller) {
      delete controllers[cancelName];
    }
  }
};
